/* MindBuilders theme JS — vanilla, no dependencies. */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  // ──────────────────────────────────────────────────────────────
  // Tiny utilities
  // ──────────────────────────────────────────────────────────────
  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const on = (el, ev, fn, opt) => el && el.addEventListener(ev, fn, opt);

  const formatMoney = (cents) => {
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  // ──────────────────────────────────────────────────────────────
  // Mobile nav
  // ──────────────────────────────────────────────────────────────
  on(document, 'click', (e) => {
    const t = e.target.closest('[data-mobile-nav-open]');
    if (t) {
      const nav = $('#mobile-nav');
      if (nav) { nav.setAttribute('aria-hidden', 'false'); document.body.classList.add('no-scroll'); }
    }
    const c = e.target.closest('[data-mobile-nav-close]');
    if (c) {
      const nav = $('#mobile-nav');
      if (nav) { nav.setAttribute('aria-hidden', 'true'); document.body.classList.remove('no-scroll'); }
    }
  });

  // ──────────────────────────────────────────────────────────────
  // Cart drawer
  // ──────────────────────────────────────────────────────────────
  const Drawer = {
    el: null,
    open() {
      this.el = $('#cart-drawer');
      if (!this.el) return;
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('no-scroll');
    },
    close() {
      const el = $('#cart-drawer');
      if (!el) return;
      el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('no-scroll');
    },
    async refresh() {
      try {
        const res = await fetch('/cart?section_id=cart-drawer', { headers: { Accept: 'text/html' } });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#cart-drawer');
        const current = $('#cart-drawer');
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
        }
        // Update header cart count
        const cart = await (await fetch('/cart.js')).json();
        $$('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          el.toggleAttribute('hidden', cart.item_count === 0);
        });
      } catch (err) {
        console.error('Cart refresh failed', err);
      }
    },
  };

  on(document, 'click', (e) => {
    const open = e.target.closest('[data-cart-open]');
    if (open) {
      e.preventDefault();
      Drawer.open();
    }
    const close = e.target.closest('[data-cart-close]');
    if (close) {
      e.preventDefault();
      Drawer.close();
    }
    const backdrop = e.target.closest('.drawer__backdrop');
    if (backdrop) Drawer.close();
  });

  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') Drawer.close();
  });

  // ──────────────────────────────────────────────────────────────
  // Cart item qty / remove (delegated)
  // ──────────────────────────────────────────────────────────────
  async function cartChange(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return res.json();
  }

  on(document, 'click', async (e) => {
    const qtyBtn = e.target.closest('[data-qty-adjust]');
    if (qtyBtn) {
      e.preventDefault();
      const line = parseInt(qtyBtn.dataset.line, 10);
      const dir = qtyBtn.dataset.qtyAdjust === 'inc' ? 1 : -1;
      const input = qtyBtn.parentElement.querySelector('input');
      const next = Math.max(0, (parseInt(input.value, 10) || 0) + dir);
      input.value = next;
      await cartChange(line, next);
      Drawer.refresh();
      return;
    }
    const rm = e.target.closest('[data-cart-remove]');
    if (rm) {
      e.preventDefault();
      const line = parseInt(rm.dataset.line, 10);
      await cartChange(line, 0);
      Drawer.refresh();
    }
  });

  // Local qty buttons (PDP) — affect the input only
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-qty-local]');
    if (!btn) return;
    e.preventDefault();
    const wrap = btn.closest('.qty');
    const input = wrap.querySelector('input');
    const dir = btn.dataset.qtyLocal === 'inc' ? 1 : -1;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + dir);
  });

  // ──────────────────────────────────────────────────────────────
  // Add-to-cart (product form + quick add)
  // ──────────────────────────────────────────────────────────────
  on(document, 'submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    const original = submit ? submit.innerHTML : '';
    if (submit) { submit.disabled = true; submit.textContent = 'Adding…'; }
    try {
      const data = new FormData(form);
      const res = await fetch('/cart/add.js', { method: 'POST', body: data });
      const result = await res.json();
      if (!res.ok) throw new Error(result.description || 'Add failed');
      await Drawer.refresh();
      Drawer.open();
    } catch (err) {
      console.error(err);
      alert('Could not add to bag: ' + err.message);
    } finally {
      if (submit) { submit.disabled = false; submit.innerHTML = original; }
    }
  });

  // ──────────────────────────────────────────────────────────────
  // Product variant picker
  // ──────────────────────────────────────────────────────────────
  function setupVariantPickers() {
    $$('[data-variant-picker]').forEach((picker) => {
      const form = picker.closest('form');
      if (!form) return;
      let data;
      try { data = JSON.parse(picker.dataset.variants); } catch { return; }
      const idInput = form.querySelector('input[name="id"]');
      const priceEl = $('[data-product-price]', form.closest('.product') || document);
      const compareEl = $('[data-product-compare]', form.closest('.product') || document);
      const submitEl = form.querySelector('[type="submit"]');
      const optionInputs = $$('input[type="radio"][data-option-index]', picker);

      const update = () => {
        const selected = {};
        optionInputs.forEach((inp) => {
          if (inp.checked) selected[inp.dataset.optionIndex] = inp.value;
        });
        const variant = data.find((v) => {
          return v.options.every((opt, i) => selected[i] === opt);
        });
        if (variant) {
          if (idInput) idInput.value = variant.id;
          if (priceEl) priceEl.textContent = formatMoney(variant.price);
          if (compareEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              compareEl.textContent = formatMoney(variant.compare_at_price);
              compareEl.hidden = false;
            } else {
              compareEl.hidden = true;
            }
          }
          if (submitEl) {
            submitEl.disabled = !variant.available;
            submitEl.querySelector('.btn-label').textContent = variant.available
              ? submitEl.dataset.labelAdd
              : submitEl.dataset.labelSoldOut;
          }
          // Update URL for shareability
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
      };

      optionInputs.forEach((inp) => on(inp, 'change', update));
      update();
    });
  }

  // ──────────────────────────────────────────────────────────────
  // Product gallery thumbs
  // ──────────────────────────────────────────────────────────────
  function setupGallery() {
    $$('[data-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      const thumbs = $$('[data-gallery-thumb]', gallery);
      thumbs.forEach((thumb) => {
        on(thumb, 'click', (e) => {
          e.preventDefault();
          thumbs.forEach((t) => t.setAttribute('aria-current', 'false'));
          thumb.setAttribute('aria-current', 'true');
          if (main) {
            main.innerHTML = '<img src="' + thumb.dataset.full + '" alt="">';
          }
        });
      });
    });
  }

  // ──────────────────────────────────────────────────────────────
  // Marquee — duplicate content for seamless loop
  // ──────────────────────────────────────────────────────────────
  function setupMarquees() {
    $$('.marquee__track:not([data-cloned])').forEach((track) => {
      track.dataset.cloned = '1';
      track.innerHTML = track.innerHTML + track.innerHTML;
    });
  }

  // ──────────────────────────────────────────────────────────────
  // Collection facets (sort)
  // ──────────────────────────────────────────────────────────────
  function setupSort() {
    $$('[data-sort]').forEach((sel) => {
      on(sel, 'change', () => {
        const url = new URL(window.location);
        url.searchParams.set('sort_by', sel.value);
        window.location = url.toString();
      });
    });
  }

  // ──────────────────────────────────────────────────────────────
  // Init
  // ──────────────────────────────────────────────────────────────
  function init() {
    setupVariantPickers();
    setupGallery();
    setupMarquees();
    setupSort();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when Shopify Theme Editor re-renders a section
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);

  window.theme = window.theme || {};
  window.theme.Drawer = Drawer;
})();
